/** Server → client one-shot event names */
export const ServerEvents = {
  PLAYER_DIED:   'player:died',
  PLAYER_KILLED: 'player:killed',
  GAME_OVER:     'game:over',
  /** Fired once per beam shot so clients can render the flash. */
  BEAM_FIRED:    'weapon:beam',
  /** Fired whenever a hit is absorbed by shields — triggers the directional shield flash. */
  SHIELD_HIT:    'shield:hit',
  /** Fired whenever a hit lands on the hull (armor takes damage). */
  HULL_HIT:      'hull:hit',
  /** Fired when a round ends — clients show the round-end overlay. */
  ROUND_END:     'round:end',
  /** Fired when the next round begins — clients hide the overlay. */
  ROUND_START:      'round:start',
  /** Fired on a meaningful ship-to-ship impulse collision. */
  SHIP_COLLISION:   'collision:ship',
  /** Fired when a ship bounces off the asteroid border wall at speed. */
  WALL_COLLISION:   'collision:wall',
  /** Fired when a player collects a powerup. */
  POWERUP_COLLECTED: 'powerup:collected',
} as const;

export type ServerEventType = typeof ServerEvents[keyof typeof ServerEvents];

export interface PlayerDiedPayload {
  sessionId: string;
  killedBy: string | null;
}

export interface PlayerKilledPayload {
  killerSessionId: string;
  victimSessionId: string;
  killerKills: number;
  killerRankIndex: number;
  victimRankIndex: number;
}

export interface GameOverPayload {
  winnerSessionId: string;
  winnerDisplayName: string;
  kills: number;
}

/**
 * Broadcast when a hit is absorbed by a player's shields.
 * The client uses this to draw a directional shield flash around the ship.
 */
export interface ShieldHitPayload {
  targetSessionId: string;
  /**
   * Impact angle in standard screen-space radians (atan2(dy, dx), y-down).
   * Points FROM the target TOWARD the attacker — i.e. the face of the shield
   * that was struck.  The client draws the arc centred on this angle.
   */
  impactAngle: number;
}

/**
 * Broadcast when armor takes a direct hit (shields were 0 or damage bled through).
 * The client uses this to draw a hull-impact spark at the struck point.
 */
export interface HullHitPayload {
  targetSessionId: string;
  /**
   * Same convention as ShieldHitPayload — points FROM the target TOWARD the attacker
   * (the face of the hull that was struck), standard screen-space atan2.
   */
  impactAngle: number;
}

/** Broadcast at the end of each round. Stats are also available in room.state.players. */
export interface RoundEndPayload {
  roundNumber: number;
}

/** Broadcast when the next round begins. */
export interface RoundStartPayload {
  roundNumber: number;
}

export interface ShipCollisionPayload {
  sessionIdA: string;
  sessionIdB: string;
}

export interface WallCollisionPayload {
  sessionId: string;
}

export interface PowerupCollectedPayload {
  collectorSessionId: string;
  type: string;
  tier: string;
}

/**
 * Broadcast when a beam weapon fires.
 * The client uses this to draw a tapered line flash in the world.
 */
export interface BeamFiredPayload {
  ownerSessionId: string;
  originX: number;
  originY: number;
  /**
   * World-space endpoint — either the surface of the first ship hit
   * or the point at max range, whichever is closer.
   */
  endX: number;
  endY: number;
  /** Hex colour from the weapon definition (e.g. 0xff3333 for RoboTAI). */
  color: number;
}
